import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bell, X } from "lucide-react";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { toast } from "sonner";
import { SafariNotificationGuide } from "./SafariNotificationGuide";

export const NotificationPermissionBanner = () => {
  const [isDismissed, setIsDismissed] = useState(false);
  const { 
    permission, 
    isSupported, 
    isSafari, 
    isStandalone, 
    isSubscribed, 
    isLoading,
    subscribe 
  } = usePushNotifications();

  // Don't show if not supported, already subscribed, or dismissed
  if (!isSupported || isSubscribed || isDismissed) {
    return null;
  }

  // Show special guide for Safari users not in standalone mode
  if (isSafari && !isStandalone) {
    return (
      <div className="mb-6">
        <SafariNotificationGuide />
      </div>
    );
  }

  // Show banner for users who granted permission but haven't completed subscription
  if (permission === 'granted' && !isSubscribed) {
    return (
      <Card className="mb-6 border-green-500/20 bg-green-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <Bell className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
            <div className="flex-1 space-y-2">
              <div>
                <p className="font-medium text-green-900">Maak je push notificatie setup compleet</p>
                <p className="text-sm text-green-700">
                  Je hebt toestemming gegeven, maar de push notificatie setup is nog niet afgerond. Klik op de knop hieronder om het proces te voltooien.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  onClick={async () => {
                    try {
                      await subscribe();
                      toast.success('Push notificaties succesvol ingeschakeld!');
                    } catch (error) {
                      console.error('Subscription error:', error);
                      toast.error('Kon notificaties niet inschakelen. Probeer het opnieuw.');
                    }
                  }}
                  size="sm"
                  disabled={isLoading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Bell className="h-4 w-4 mr-2" />
                  {isLoading ? 'Bezig...' : 'Setup Voltooien'}
                </Button>
                <Button 
                  onClick={() => setIsDismissed(true)} 
                  variant="ghost" 
                  size="sm"
                >
                  Niet nu
                </Button>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => setIsDismissed(true)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show banner for users who can enable notifications
  if (permission === 'default') {
    return (
      <Card className="mb-6 border-primary/20 bg-primary/5">
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <Bell className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div className="flex-1 space-y-2">
              <div>
                <p className="font-medium">Mis geen belangrijke updates</p>
                <p className="text-sm text-muted-foreground">
                  Ontvang direct push notificaties voor nieuwe leads, to-do's, en projectupdates - zelfs wanneer de app niet open is.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  onClick={async () => {
                    try {
                      await subscribe();
                      toast.success('Push notificaties ingeschakeld!');
                    } catch (error) {
                      console.error('Subscription error:', error);
                      toast.error('Kon notificaties niet inschakelen. Probeer het opnieuw.');
                    }
                  }}
                  size="sm"
                  disabled={isLoading}
                >
                  <Bell className="h-4 w-4 mr-2" />
                  {isLoading ? 'Bezig...' : 'Inschakelen'}
                </Button>
                <Button 
                  onClick={() => setIsDismissed(true)} 
                  variant="ghost" 
                  size="sm"
                >
                  Niet nu
                </Button>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => setIsDismissed(true)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Don't show anything if permission was denied
  return null;
};
